import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import TextInput from "../Input/TextInput";
import { FONT_INTER } from "../../../styles/fonts";
import { compareStrings } from "../../../utils/compareStrings";

const ACCENT_COLOR = "#185CFB";
const ACTIVE_COLOR = "#2E7D32";
const INACTIVE_COLOR = "#D32F2F";

// Active items (when `active` is present) before inactive, alphabetical within each group.
// Items without `active` (e.g. Recursos) fall through to plain alphabetical order.
const sortItems = (items) =>
  [...items].sort((a, b) => {
    const activeDiff = Number(Boolean(b.active)) - Number(Boolean(a.active));
    if (activeDiff !== 0) return activeDiff;
    return compareStrings(a.label, b.label);
  });

// One panel of a Miller-columns layout: search + selectable list + empty/loading states.
// Purely presentational - items are already the rows to render ({ id, label, description }),
// filtering/fetching is the caller's responsibility.
const ListColumn = ({
  title,
  items,
  selectedId,
  onSelect,
  searchTerm = "",
  onSearchChange,
  searchPlaceholder = "Buscar...",
  searchable = true,
  loading = false,
  emptyMessage = "Nenhum item encontrado.",
  renderItemAction,
  sortAlphabetically = true,
  sx,
}) => {
  const displayItems = sortAlphabetically ? sortItems(items) : items;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: 0,
        height: "100%",
        ...sx,
      }}
    >
      {title && (
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontWeight: 600,
            fontSize: 14,
            color: "#1a1a2e",
            px: 2,
            pt: 2,
            pb: searchable ? 1 : 2,
          }}
        >
          {title}
        </Typography>
      )}

      {searchable && (
        <Box sx={{ px: 2, pb: 1 }}>
          <TextInput
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(event) => onSearchChange?.(event.target.value)}
            height="36px"
          />
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} sx={{ color: ACCENT_COLOR }} />
          </Box>
        ) : items.length === 0 ? (
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 13,
              color: "#6B6B70",
              textAlign: "center",
              py: 4,
              px: 2,
            }}
          >
            {emptyMessage}
          </Typography>
        ) : (
          <List disablePadding>
            {displayItems.map((item) => {
              const isSelected = item.id === selectedId;

              return (
                <ListItem
                  key={item.id}
                  disablePadding
                  secondaryAction={renderItemAction ? renderItemAction(item) : null}
                >
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => onSelect?.(item.id)}
                    sx={{
                      py: 1,
                      px: 2,
                      pr: renderItemAction ? 6 : 2,
                      "&.Mui-selected": {
                        backgroundColor: "rgba(24, 92, 251, 0.08)",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "rgba(24, 92, 251, 0.12)",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        item.active === undefined ? (
                          item.label
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <Tooltip title={item.active ? "Ativo" : "Inativo"}>
                              <FiberManualRecordIcon
                                aria-label={item.active ? "Ativo" : "Inativo"}
                                sx={{
                                  fontSize: 8,
                                  color: item.active ? ACTIVE_COLOR : INACTIVE_COLOR,
                                  flexShrink: 0,
                                }}
                              />
                            </Tooltip>
                            <Box component="span">{item.label}</Box>
                          </Box>
                        )
                      }
                      secondary={item.description}
                      primaryTypographyProps={{
                        sx: {
                          fontFamily: FONT_INTER,
                          fontSize: 14,
                          fontWeight: isSelected ? 600 : 400,
                          color: "#1a1a2e",
                        },
                      }}
                      secondaryTypographyProps={{
                        sx: { fontFamily: FONT_INTER, fontSize: 12, color: "#6B6B70" },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default ListColumn;
